/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { IAccountData } from '@app/models/account-data.model';
import { IBorrowerCoBorrowerAge } from '@app/models/borrower.model';
import { IBundle, IBundles } from '@app/models/bundles.model';
import { IErrorFieldObject } from '@app/models/error-fields.model';
import {
  eProductNames,
  eProductRateNames,
  IFiProducts,
  ProductFormValues
} from '@app/models/fi-products.model';
import { StoredMessage } from '@app/models/isa.model';
import {
  ILoanCalculatorForm,
  ILoanCalculatorResponse
} from '@app/models/loan-calculator-response.model';
import { LoanStatus } from '@app/models/Loan-Status';
import { LoanType } from '@app/models/Loan-Type';
import { Pages } from '@app/models/Pages';
import {
  getNewParentRateRequest,
  IProductPyramid,
  IRateRequest,
  IRateRequestPartial,
  VehicleExceptionAndOption
} from '@app/models/rate-request.model';
import {
  CvpRate,
  GapRate,
  getEmptyCurrentSelectedProductRates,
  getEmptyRateResponse,
  ICurrentSelectedProductRates,
  IRateResponse,
  KeyProtectionRate,
  MmpRate,
  PaymentProtectionRate,
  PpmRate,
  SdRate,
  UProductRate,
  UProductRates
} from '@app/models/rate-response.model';
import { IUserAccMarketPlace } from '@app/models/user-info.model';
import {
  BillingUserSelection,
  BillingUserSelectionStorage
} from '@app/modules/billing-reconciliation/billing-storage.model';
import {
  DeepObjectCopy,
  filterPpiRatesByAge,
  PPIAgeEligibility
} from '@app/shared/utils/utility';
import * as moment from 'moment';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class UnityDirectStateService {
  private _isNewProposal: boolean;
  public isGapEligible: boolean = true;
  private _proposalId: number | string;
  defaultMapr: boolean = true;
  private _bundleId: number;
  private isBundlesEnabled: boolean;
  private _userInfo: IUserAccMarketPlace;
  private _userAccount: IAccountData;
  private _fiProducts: IFiProducts;
  private _isVehicle: boolean;
  private _isVehicleNotFound: boolean;
  public maprBorrowerChecked: boolean = false;
  public calculationMsg: string;
  public swbcFee: number;
  public invalidFieldsList: IErrorFieldObject[] = [];
  public callNadaAdvancedValue: boolean = false;
  public isVehicleValueReturned: boolean = false;
  public areCustomRatesUpdated: boolean = false;
  public isProductsLoaded: boolean = false;
  public vehicleValue: string;
  public valuationDialogClicked: boolean = false;
  public loanValue: number = 0;
  private _accountEligibleProdNamesList: eProductNames[] = [];
  private currentSelectedProducts: Array<eProductNames> = [];
  private productPyramid: IProductPyramid;
  private rateRequest: IRateRequest = getNewParentRateRequest();
  private productRates: Partial<IRateResponse>;
  private productFormValues: Partial<ProductFormValues> = {};
  private _totalAmountFinanced: number = 0;
  private currentSelectedProductRates: Partial<ICurrentSelectedProductRates> =
    null;
  private paymentData: ILoanCalculatorResponse;
  private loanFormValue: ILoanCalculatorForm;
  private mmpPlanOptionsSelected: string[] = [];
  private tlrDeclined: boolean = false;
  private mmpComparisonRates: MmpRate[] | null = null;
  private skipProductsRating: eProductNames[] = [];
  private prodSelectedRateMap: Map<eProductNames, UProductRate> = new Map<
    eProductNames,
    UProductRate
  >();
  private userDisabledProducts: eProductNames[] = [];
  private _proposalPayment: number;
  private _loanStatus: LoanStatus = LoanStatus.New;
  private _proposalBundleId: number;
  private _isVinValid: boolean;
  private _isAppDirect: boolean;
  private _canUserAccessNewQuote: boolean;
  private _canUserModifyBooking: boolean;
  private _canUserModifyBilling: boolean;
  private _canUserAccessBilling: boolean;
  private _canUserAccessAndModifyAdminClientApp: boolean;
  private _mockVin: string;
  private _isNavInProgress: boolean;
  private _isUserClosedISA: boolean = false;
  private _billingUserSelection: BillingUserSelectionStorage = {};
  private _isAdminUser: boolean = false;
  private _monthlyPayment: number = null;
  private _isShowEstimatedMonthlyPayment: boolean = false;
  private _isSetElectricCode: boolean = false;
  isProductsRatesAvailableSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  isCalcReadyForRerate: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  currentSelectedProductsSubject: BehaviorSubject<eProductNames[]> =
    new BehaviorSubject<eProductNames[]>(this.currentSelectedProducts);
  userDisabledProductsSubject: BehaviorSubject<eProductNames[]> =
    new BehaviorSubject<eProductNames[]>(this.userDisabledProducts);
  currentStep: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  isCalcPaymentSubject: Subject<boolean> = new Subject<boolean>();
  isSummarySubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  isPaymentInfoAvailableSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  isRatingInProgressSubject: Subject<boolean> = new Subject<boolean>();
  isResetUDFormSubject: Subject<boolean> = new Subject<boolean>();
  loanStatusSubject: Subject<LoanStatus> = new Subject<LoanStatus>();
  createProposalSubject: Subject<boolean> = new Subject<boolean>();
  portalHeaderAvailableSubject: Subject<boolean> = new Subject<boolean>();
  noPPIRateAfterAgeFilter: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  // Emits true when only one borrower meets the PP age restriction, so the UI
  // can show the single-coverage-only message while still displaying rates.
  partialPPIRateAfterAgeFilter: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  // Emits true when PP should auto-select (new quote, both eligible).
  autoSelectPpiSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  isResetVehicleFormSubject: Subject<boolean> = new Subject<boolean>();
  openBillingBehaviorSub$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  isVinConfirmed$: Subject<boolean> = new Subject<boolean>();
  commercialSettingSubject: Subject<object> = new Subject<object>();
  saveBorrowerInfoSubject: BehaviorSubject<void> = new BehaviorSubject<void>(
    null
  );
  isaOpenSubject: Subject<boolean> = new Subject<boolean>();
  navInProgressSubject: Subject<boolean> = new Subject<boolean>();
  coverageDate: any;
  borrowerCoBorrowerAge: IBorrowerCoBorrowerAge = {
    borrowerAge: 0,
    coBorrowerAge: 0
  };
  // Caches the full (pre-age-filter) PP rates from the latest rate response so
  // eligibility can be re-evaluated when borrower ages change without a new
  // rate call (e.g. correcting a DOB from ineligible to eligible).
  private _fullPaymentProtectionRates: PaymentProtectionRate[] = [];
  bundlesData: IBundle[];
  _vehicleExceptionAndOption: VehicleExceptionAndOption;
  private _vehicleYMMSMap: Map<
    string,
    (string | number | { code: string; description: string })[]
  > = new Map<
    string,
    (string | number | { code: string; description: string })[]
  >();
  private readonly newQuoteChatSubject = new BehaviorSubject<StoredMessage[]>(
    []
  );
  readonly newQuoteChat$ = this.newQuoteChatSubject.asObservable();
  isVehicleVinYMMSEvalDone: Subject<boolean> = new Subject<boolean>();
  private readonly vehiclePurchasePriceDraftSubject = new BehaviorSubject<
    number | null
  >(null);
  vehiclePurchasePriceDraft$ =
    this.vehiclePurchasePriceDraftSubject.asObservable();

  constructor() { }

  set isSetElectricCode(value: boolean) {
    this._isSetElectricCode = value;
  }
  get isSetElectricCode(): boolean {
    return this._isSetElectricCode;
  }

  /**
   * Set and Get monthlyPayment
   */
  set monthlyPayment(value: number) {
    this._monthlyPayment = value;
  }

  get monthlyPayment(): number {
    return this._monthlyPayment;
  }

  /**
   * Set and Get Vehicle YMMS details.
   * Useful in case of Express where details of Vehicle Popup can be stored for retrieval in Vehicle Component
   * @param ymmsMap {Map<string, string[] | number[]>}
   */
  set vehicleYMMSMap(
    ymmsMap: Map<
      string,
      (string | number | { code: string; description: string })[]
    >
  ) {
    this._vehicleYMMSMap = ymmsMap;
  }

  get vehicleYMMSMap(): Map<
    string,
    (string | number | { code: string; description: string })[]
  > {
    return this._vehicleYMMSMap;
  }

  /**
   * Method to set borrower & co borrower Age
   * @param {number} borrowerAge
   * @param {number} coBorrowerAge
   * @returns {void}
   */
  setBorrowerCoBorrowerAge(borrowerAge: number, coBorrowerAge: number): void {
    this.borrowerCoBorrowerAge.borrowerAge = borrowerAge;
    this.borrowerCoBorrowerAge.coBorrowerAge = coBorrowerAge;
  }
  /**
   * Method to get borrower & co borrower Age
   * @returns {IBorrowerCoBorrowerAge}
   */
  getBorrowerCoBorrowerAge(): IBorrowerCoBorrowerAge {
    return this.borrowerCoBorrowerAge;
  }

  /**
   * Caches the full, unfiltered PP rates from a rate response so that PP age
   * eligibility can later be re-evaluated when ages change.
   * @param rates {PaymentProtectionRate[]}
   */
  setFullPaymentProtectionRates(rates: PaymentProtectionRate[]): void {
    this._fullPaymentProtectionRates = rates ?? [];
  }

  /**
   * Re-evaluates PP age eligibility against the cached full rate list using the
   * current borrower/co-borrower ages, and re-emits the age-filter subjects.
   * Lets the PPI checkbox/banner react to a DOB correction without a new rate
   * call. Does nothing when the age rule is off or no rates are cached.
   */
  reevaluatePpiAgeEligibility(): void {
    if (
      !this.userAccount?.['isEnablePaymentProtectionAgeRule'] ||
      !this._fullPaymentProtectionRates?.length
    ) {
      return;
    }
    const { eligibility, isPartialDueToIneligibleCoApplicant } =
      filterPpiRatesByAge(
        this._fullPaymentProtectionRates,
        this.borrowerCoBorrowerAge
      );
    this.noPPIRateAfterAgeFilter.next(
      eligibility === PPIAgeEligibility.NoneEligible
    );
    this.partialPPIRateAfterAgeFilter.next(
      isPartialDueToIneligibleCoApplicant
    );
  }

  /** Emits auto-select: age rule on, PP rates cached, new proposal, both eligible, not user-disabled. */
  evaluatePpiAutoSelect(): void {
    if (
      !this.userAccount?.['isEnablePaymentProtectionAgeRule'] ||
      !this._fullPaymentProtectionRates?.length ||
      !this.isNewProposal ||
      this.userDisabledProducts.includes(eProductNames.PPI)
    ) {
      this.autoSelectPpiSubject.next(false);
      return;
    }
    const { eligibility } = filterPpiRatesByAge(
      this._fullPaymentProtectionRates,
      this.borrowerCoBorrowerAge
    );
    this.autoSelectPpiSubject.next(
      eligibility === PPIAgeEligibility.BothEligible
    );
  }

  /** Resets/Clears the state
   * Use cases - when products is hidden due to change in top panels, loading proposal from reivew
   * Add more as the state expands.
   */
  resetState(): void {
    this.isNewProposal = true;
    this.proposalId = null;
    this.currentSelectedProducts = [];
    this.productPyramid = null;
    this.productRates = {};
    this.productFormValues = {};
    this.totalAmountFinanced = 0;
    this.currentSelectedProductRates = null;
    this.paymentData = null;
    this.mmpPlanOptionsSelected = [];
    this.tlrDeclined = false;
    this.skipProductsRating = [];
    this.prodSelectedRateMap.clear();
    this.userDisabledProducts = [];
    this.proposalPayment = null;
    this.loanStatus = LoanStatus.New;
    this.isVinValid = false;
    this.isAppDirect = undefined;
    this._isVehicleNotFound = false;
    this._isUserClosedISA = false;
    this._monthlyPayment = null;
    this._fullPaymentProtectionRates = [];
    this.autoSelectPpiSubject.next(false);
    this.clearNewQuoteChat();
  }

  /**
   * Resets Products related cache. Use this when Vehicle/Borrower/Loan Info is being changed
   */
  resetProductsState(): void {
    this.currentSelectedProducts = [];
    this.productPyramid = null;
    this.productRates = {};
    this.productFormValues = {};
    this.totalAmountFinanced = 0;
    this.currentSelectedProductRates = null;
    this.paymentData = null;
    this.mmpPlanOptionsSelected = [];
    this.tlrDeclined = false;
    this.mmpComparisonRates = null;
    this.skipProductsRating = [];
    this.prodSelectedRateMap.clear();
    this.userDisabledProducts = [];
    this.proposalPayment = null;
    this._proposalBundleId = null;
  }

  /**
   * Set and Get if App is Direct or not (if not, it is Express)
   * @param isDirect {boolean}
   */
  set isAppDirect(isDirect: boolean) {
    this._isAppDirect = isDirect;
  }

  get isAppDirect(): boolean {
    return this._isAppDirect;
  }

  /**
   * Set bundle id in proposal
   * @param bundle id {number}
   */
  set proposalBundleId(id: number) {
    this._proposalBundleId = id;
  }

  /**
   * Get bundle id in proposal
   * @returns bundle id {number}
   */
  get proposalBundleId(): number {
    return this._proposalBundleId;
  }

  /**
   * set and get whether it's a New proposal or an Existing proposal
   */
  set isNewProposal(isNew: boolean) {
    this._isNewProposal = isNew;
  }

  get isNewProposal(): boolean {
    return this._isNewProposal;
  }

  set proposalId(id: number | string) {
    this._proposalId = id;
  }

  get proposalId(): number | string {
    return this._proposalId;
  }

  set bundleId(id: number) {
    this._bundleId = id;
  }

  get bundleId(): number {
    return this._bundleId;
  }

  /**
   * Set and Get validity of VIN as a {boolean}
   */
  set isVinValid(valid: boolean) {
    this._isVinValid = valid;
  }

  get isVinValid(): boolean {
    return this._isVinValid;
  }

  /**
   * Portal Account Id set and get
   * @param userInfo
   */
  set userInfo(userInfo: IUserAccMarketPlace) {
    this._userInfo = userInfo;
  }

  get userInfo(): IUserAccMarketPlace {
    return this._userInfo;
  }

  /**
   * User Account set and get
   * @param acc
   */
  set userAccount(acc: IAccountData) {
    this._userAccount = acc;
  }

  get userAccount(): IAccountData {
    return this._userAccount;
  }

  /**
   * Products available for account set and get
   * @param prods
   */
  set fiProducts(prods: IFiProducts) {
    this._fiProducts = prods;
  }

  get fiProducts(): IFiProducts {
    return this._fiProducts;
  }

  /**
   * List of Product Names eligible for the account
   * @param prods
   */
  set accountEligibleProductsList(prods: eProductNames[]) {
    this._accountEligibleProdNamesList = prods;
  }

  get accountEligibleProductsList(): eProductNames[] {
    return this._accountEligibleProdNamesList;
  }

  /**
   * Bundles flag for Package Options set and get
   * @param isBundlesEnabled
   */

  setBundlesEnabled(isBundlesEnabled: boolean) {
    this.isBundlesEnabled = isBundlesEnabled;
  }
  getBundlesEnabled() {
    return this.isBundlesEnabled;
  }

  /**
   * To set and get bundles Data
   * @param {IBundles} bundlesData
   */

  setBundlesData(bundlesData: IBundles): void {
    this.bundlesData = bundlesData.bundles;
  }
  getBundlesData(): IBundle[] {
    return this.bundlesData;
  }

  /**
   * Current Selection of Products set and get
   * @param product
   * @returns
   */
  setCurrentSelectedProducts(product: eProductNames): void {
    if (this.currentSelectedProducts.includes(product)) return;
    this.currentSelectedProducts.push(product);
    this.currentSelectedProductsSubject.next(this.currentSelectedProducts);
  }

  getCurrentSelectedProducts(): Array<eProductNames> {
    return this.currentSelectedProducts;
  }

  /**
   * Manipulate the current selection of products given a product name
   * @param product
   */
  removeProductFromSelected(product: eProductNames): void {
    const idx = this.currentSelectedProducts.indexOf(product);
    if (idx !== -1) {
      this.currentSelectedProducts.splice(idx, 1);
    }
    this.currentSelectedProducts = this.currentSelectedProducts.filter(
      () => product
    );
    this.currentSelectedProductsSubject.next(this.currentSelectedProducts);
    this.removeProductRates(product);
  }

  /**
   * Product pyramid set and get
   * @param pyramidObj
   */
  setProductPyramid(pyramidObj: IProductPyramid) {
    if (this.productPyramid) {
      this.productPyramid = JSON.parse(JSON.stringify(pyramidObj));
    } else {
      this.productPyramid = pyramidObj;
    }
  }

  getProductPyramid(): IProductPyramid {
    return this.productPyramid;
  }

  /**
   * Returns Coverage date
   */
  getCoverageDate(): string {
    return this.coverageDate;
  }
  /**
   * Updates coverage date
   * @param value
   */
  updateCoverageDateInRateReq(value: string): void {
    this.coverageDate = moment(value);
  }
  /**
   * Rate Request obj set and get
   * @param rateReqObj
   */
  setRateRequestValues(rateReqObj: IRateRequestPartial): void {
    if (rateReqObj.vehicle.collateralType === 0) {
      rateReqObj.loanData.loanType = LoanType.Vehicle;
    } else if (rateReqObj.vehicle.collateralType > 0) {
      rateReqObj.loanData.loanType = LoanType.OtherCollateral;
    }
    if (rateReqObj.loanData?.deductions === null) {
      rateReqObj.loanData.deductions = 0;
    }
    if (rateReqObj.loanData?.otherFees === null) {
      rateReqObj.loanData.otherFees = 0;
    }
    this.rateRequest = DeepObjectCopy(this.rateRequest, rateReqObj);
  }

  getRateRequestValues(): IRateRequest {
    if (!this.rateRequest.vehicle.vin) {
      this.rateRequest.vehicle.isVehicleManuallySelected = true;
    }
    return this.rateRequest;
  }

  /**
   * Update Vehicle purchase price
   * @param purchasePrice
   */
  updatePurchasePrice(purchasePrice: number): void {
    this.rateRequest.vehicle.purchasePrice = purchasePrice;
  }

  /**
   * isVehicle/isVehicleNotFound loan type flag set and get
   * @param loanType
   */
  setLoanTypeFlags(loanType: number): void {
    if (loanType == LoanType.Vehicle) {
      this._isVehicle = true;
      this._isVehicleNotFound = false;
    } else if (loanType == LoanType.OtherCollateral) {
      this._isVehicle = false;
      this._isVehicleNotFound = false;
    } else if (loanType == LoanType.VehicleNotFound) {
      this._isVehicleNotFound = true;
      this._isVehicle = true;
    }
  }

  /**
   * Returns if vehicle loan type
   */
  get isVehicle() {
    return this._isVehicle;
  }

  /**
   * Returns if vehicle undetected sub-type
   */
  get isVehicleNotFound() {
    return this._isVehicleNotFound;
  }

  /**
   * Product rates set and get
   * @param rates
   */
  setProductsRatesResponse(
    rates: IRateResponse,
    productFormValues?: ProductFormValues
  ): void {
    const ratesKeys = Object.keys(rates);
    if (
      rates?.mmpRates == null &&
      rates?.vehicleReturnRates == null &&
      rates?.gapRates == null
    ) {
      this.setUserDisabledProducts(eProductNames.MMP);
    }
    // Populate all rates in a single object. So, if a rate is null in response, delete that key and copy over the same object.
    ratesKeys.forEach(
      (key: string) => rates[key] === null && delete rates[key]
    );
    if (this.productRates === null || this.productRates === undefined) {
      this.productRates = {};
    }
    this.productRates = Object.assign(this.productRates, rates);
    if (
      this.productRates.mmpRates?.length ||
      this.productRates.ppmRates?.length ||
      this.productRates.keyProtectionRates?.length ||
      this.productRates.gapRates?.length ||
      this.productRates.vehicleReturnRates?.length ||
      this.productRates.pepRates?.length ||
      this.productRates.paymentProtectionRates?.length ||
      this.productRates.sdRates?.length ||
      this.productRates.cvpRates?.length
    ) {
      this.isProductsRatesAvailableSubject.next(true);
      this.setCurrentSelectedProductRates(null, productFormValues);
    }
  }

  getProductsRatesResponse(): Partial<IRateResponse> {
    return this.productRates;
  }

  /**
   * When a product is unselected, remove it's corresponding rates
   * @param product
   */
  removeProductRates(product?: eProductNames, setEmpty?: boolean): void {
    if (this.productRates) {
      if (setEmpty) {
        this.productRates = null;
      } else if (product && this.productRates[product + 'Rates']) {
        this.productRates[product + 'Rates'] = null;
        this.currentSelectedProductRates[product + 'Rate'] = null;
      }
    }
  }

  /**
   * Set default rate selection or store and get the user selection using these methods. Relies on productRates to be informed about rates.
   * This method will be called for every rate call and iteratively store the product rate only if not null.
   */
  setCurrentSelectedProductRates(
    setEmpty?: boolean,
    productFormValues?: ProductFormValues
  ): void {
    // If current selection is null, set default
    // Add more logic on saving selection based on values NOT on rateID
    if (setEmpty) {
      this.currentSelectedProductRates = null;
    }

    if (!this.currentSelectedProductRates) {
      this.currentSelectedProductRates = getEmptyCurrentSelectedProductRates();
    }

    if (!this.productRates) {
      this.productRates = getEmptyRateResponse();
    }

    for (const product in eProductNames) {
      if (this.productRates[eProductNames[product] + 'Rates']?.length) {
        if (productFormValues) {
          const propKey = `selected${eProductRateNames[eProductNames[product]]}Rate`;
          const prod = eProductNames[product];
          const prodRateId = productFormValues?.[prod]?.[propKey]?.rateId;
          let rateIdx;
          if (prodRateId) {
            rateIdx = this.productRates[
              eProductNames[product] + 'Rates'
            ].findIndex((rate: any) => rate.rateId === prodRateId);
          }
          rateIdx = rateIdx === -1 || rateIdx === undefined ? 0 : rateIdx;
          this.currentSelectedProductRates[eProductNames[product] + 'Rate'] =
            this.productRates[eProductNames[product] + 'Rates'][rateIdx];
        } else {
          this.currentSelectedProductRates[eProductNames[product] + 'Rate'] =
            this.productRates[eProductNames[product] + 'Rates'][0];
        }

        const idx = this.userDisabledProducts.indexOf(eProductNames[product]);
        if (
          this.productRates[`${eProductNames[product]}Rates`]?.length &&
          idx !== -1
        ) {
          this.userDisabledProducts.splice(idx, 1);
          this.userDisabledProductsSubject.next(this.userDisabledProducts);
        }
      }
    }
  }

  getCurrentSelectedProductRates(): Partial<ICurrentSelectedProductRates> {
    return this.currentSelectedProductRates;
  }

  /**
   * Get rate of a specific product
   * @param product the name of the product for which the rate is being requested
   * @returns the product rate
   */
  getRatesByProduct(product?: eProductNames): UProductRates {
    const pRate = product + 'Rates';
    return this.productRates?.[pRate];
  }

  /**
   * Set and get the selected rate for each product
   * @param prod name of the product
   * @param rate Union of each product rate
   */
  setProdSelectedRateMap(prod: eProductNames, rate: UProductRate): void {
    this.prodSelectedRateMap.set(prod, rate);
  }

  getProdSelectedRateMap(
    prod?: eProductNames
  ): Map<eProductNames, UProductRate> | UProductRate {
    if (prod) {
      return this.prodSelectedRateMap.has(prod)
        ? this.prodSelectedRateMap.get(prod)
        : null;
    } else {
      return this.prodSelectedRateMap;
    }
  }

  /**
   * Returns true if the any selected rate is modified from default rate
   * @returns {boolean}
   */
  isMapUpdated(): boolean {
    if (this.prodSelectedRateMap.size > 0) return true;
    else return false;
  }

  /**
   * Get the selected PPI Rate for Calculating payment
   * @returns {PaymentProtectionRate}
   */
  getSelectedPPIRate(): PaymentProtectionRate {
    return this.prodSelectedRateMap.get(
      eProductNames.PPI
    ) as PaymentProtectionRate;
  }

  /**
   * Get the selected MMP Rate for Calculating payment
   * @returns {MmpRate}
   */
  getSelectedMMPRate(): MmpRate {
    return this.prodSelectedRateMap.get(eProductNames.MMP) as MmpRate;
  }

  /**
   * Get the selected PPM Rate for Calculating payment
   * @returns {PpmRate}
   */
  getSelectedPPMRate(): PpmRate {
    return this.prodSelectedRateMap.get(eProductNames.PPM) as PpmRate;
  }

  /**
   * Get the selected GAP Rate for Calculating payment
   * @returns {GapRate}
   */
  getSelectedGAPRate(): GapRate {
    return this.prodSelectedRateMap.get(eProductNames.GAP) as GapRate;
  }

  /**
   * Get the selected Key Protection Rate for Calculating payment
   * @returns {KeyProtectionRate}
   */
  getSelectedKeyProtectionRate(): KeyProtectionRate {
    return this.prodSelectedRateMap.get(eProductNames.KP) as KeyProtectionRate;
  }

  /**
   * Get the selected CVP Rate for Calculating payment
   * @returns {CvpRate}
   */
  getSelectedCVPRate(): CvpRate {
    return this.prodSelectedRateMap.get(eProductNames.CVP) as CvpRate;
  }

  /**
   * Get the selected SD (Screen Protection) Rate for Calculating payment
   * @returns {SdRate}
   */
  getSelectedSDRate(): SdRate {
    return this.prodSelectedRateMap.get(eProductNames.SD) as SdRate;
  }

  /**
   * Product form values set and get
   * @param formValues
   */
  setProductFormValues(formValues: ProductFormValues): void {
    this.productFormValues = { ...formValues };
  }

  getProductFormValues(product: eProductNames): any {
    return this.productFormValues[product];
  }

  /**
   * Set and get Total Amount Financed.
   * @param taf as number
   */
  set totalAmountFinanced(taf: number) {
    this._totalAmountFinanced = taf;
  }

  get totalAmountFinanced() {
    return this._totalAmountFinanced;
  }

  /**
   * Set and get payment data once the Payment API is called.
   * @param paymentData of type ILoanCalculatorResponse
   */
  setLoanCalculatorResponse(
    paymentData: ILoanCalculatorResponse,
    loanFormValue?: ILoanCalculatorForm
  ): void {
    this.paymentData = paymentData;
    this.loanFormValue = loanFormValue;
    if (this.paymentData?.estimatedPayment) {
      this.isPaymentInfoAvailableSubject.next(true);
      this.isSummarySubject.next(true);
    } else {
      this.isPaymentInfoAvailableSubject.next(false);
    }
  }

  getLoanCalculatorResponse(): ILoanCalculatorResponse {
    return this.paymentData;
  }

  getLoanFormDetails(): ILoanCalculatorForm {
    return this.loanFormValue;
  }

  /**
   * Set and Get Mmp plan options
   * @param optIdArr array of strings with the plan options ids
   */
  setMmpPlanOptionsSelected(optIdArr: string[]): void {
    this.mmpPlanOptionsSelected = optIdArr;
  }

  getMmpPlanOptionsSelected(): string[] {
    return this.mmpPlanOptionsSelected || [];
  }

  /**
   * Set/Get whether the user deliberately declined a pre-selected TLR, as
   * opposed to it not yet being auto-selected on load, so the TAF doesn't
   * re-add its cost. Only relevant for preSelectTotalLossRefund accounts.
   */
  setTlrDeclined(declined: boolean): void {
    this.tlrDeclined = declined;
  }

  getTlrDeclined(): boolean {
    return this.tlrDeclined;
  }

  /**
   * Stores the user's custom MMP comparison rate selection so it persists
   * across navigation (e.g. to the review page and back).
   */
  setMmpComparisonRates(rates: MmpRate[] | null): void {
    this.mmpComparisonRates = rates;
  }

  getMmpComparisonRates(): MmpRate[] | null {
    return this.mmpComparisonRates;
  }

  /**
   * Set and Get products to be skipped for rerating
   * @param products eProductNames
   */
  setSkipProductsRating(products: eProductNames[]): void {
    this.skipProductsRating = products;
  }

  getSkipProductsRating(): eProductNames[] {
    return this.skipProductsRating;
  }

  /**
   * Set and Get products that have been deselected by user
   * @params prodName eProductNames
   */
  setUserDisabledProducts(prodName: eProductNames): void {
    if (!this.userDisabledProducts.includes(prodName)) {
      this.userDisabledProducts.push(prodName);
    }
    this.userDisabledProductsSubject.next(this.userDisabledProducts);
  }

  getUserDisabledProducts(): eProductNames[] {
    return this.userDisabledProducts;
  }

  /**
   * To remove user disabled products when products are re-selected
   * @params prodName eProductNames
   */
  removeUserDisabledProducts(prodName: eProductNames): void {
    this.userDisabledProducts = this.userDisabledProducts.filter(
      product => product !== prodName
    );
    this.userDisabledProductsSubject.next(this.userDisabledProducts);
  }

  /**
   * Set and get proposal payment field
   * @params payment number
   */
  set proposalPayment(payment: number) {
    this._proposalPayment = payment;
  }

  get proposalPayment(): number {
    return this._proposalPayment;
  }

  /**
   * Set and Get Loan status
   * @param status {LoanStatus}
   */
  set loanStatus(status: LoanStatus) {
    this._loanStatus = status;
  }

  get loanStatus(): LoanStatus {
    return this._loanStatus;
  }

  /**
   * Set and Get flag to show New Quote button
   * @param {boolean}
   */
  set canUserAccessNewQuote(val: boolean) {
    this._canUserAccessNewQuote = val;
  }

  get canUserAccessNewQuote(): boolean {
    return this._canUserAccessNewQuote;
  }

  /**
   * Set and Get Booking modify flag
   * @param {boolean}
   */
  set canUserModifyBooking(val: boolean) {
    this._canUserModifyBooking = val;
  }

  get canUserModifyBooking(): boolean {
    return this._canUserModifyBooking;
  }

  /**
   * Set and Get Billing modify flag
   * @param {boolean}
   */
  set canUserModifyBilling(val: boolean) {
    this._canUserModifyBilling = val;
  }

  get canUserModifyBilling(): boolean {
    return this._canUserModifyBilling;
  }

  /**
   * Set and Get flag to show Billing
   * @param {boolean}
   */
  set canUserAccessBilling(val: boolean) {
    this._canUserAccessBilling = val;
  }

  get canUserAccessBilling(): boolean {
    return this._canUserAccessBilling;
  }

  set canUserAccessAndModifyAdminClientApp(val: boolean) {
    this._canUserAccessAndModifyAdminClientApp = val;
  }

  get canUserAccessAndModifyAdminClientApp(): boolean {
    return this._canUserAccessAndModifyAdminClientApp;
  }

  /**
   * Getter to decide on Direct routing depending on privilege
   */
  get directDefaultRoute(): string {
    return this.canUserAccessNewQuote ? '/' : Pages.Billing;
  }

  /**
   * Set and Get mockVin value
   */
  set mockVin(vin: string) {
    this._mockVin = vin;
  }

  get mockVin(): string {
    return this._mockVin;
  }

  /**
   * Set and Get navigation progress state
   */
  set isNavInProgress(inProgress: boolean) {
    this._isNavInProgress = inProgress;
  }

  get isNavInProgress(): boolean {
    return this._isNavInProgress;
  }

  /**
   * Set and Get ISA closed state state
   */
  set isUserClosedISA(isClosed: boolean) {
    this._isUserClosedISA = isClosed;
  }

  get isUserClosedISA(): boolean {
    return this._isUserClosedISA;
  }

  /**
   * Set and Get billing filters
   */
  setBillingFilters(dateRange: string, filterData: BillingUserSelection) {
    this._billingUserSelection[dateRange] = filterData;
  }

  getBillingFilters(): BillingUserSelectionStorage {
    return this._billingUserSelection;
  }

  set vehicleExceptionAndOption(options: VehicleExceptionAndOption) {
    this._vehicleExceptionAndOption = options;
  }

  get vehicleExceptionAndOption(): VehicleExceptionAndOption {
    return this._vehicleExceptionAndOption;
  }

  /**
   * Returns chat saved for New Quotes without Proposal ID
   * @returns {StoredMessage[]}
   */

  getNewQuoteChat(): StoredMessage[] {
    return this.newQuoteChatSubject.value;
  }

  /**
   * Saves New Quote Chat in rxjs subject
   * @param messages {StoredMessage[]}
   */
  setNewQuoteChat(messages: StoredMessage[]): void {
    this.newQuoteChatSubject.next(messages);
  }

  /**
   * Clears any existing temp chat
   */
  clearNewQuoteChat(): void {
    this.newQuoteChatSubject.next([]);
  }

  set isAdminUser(flag: boolean) {
    this._isAdminUser = flag;
  }

  get isAdminUser(): boolean {
    return this._isAdminUser;
  }

  set isShowEstimatedMonthlyPayment(flag: boolean) {
    this._isShowEstimatedMonthlyPayment = flag;
  }

  get isShowEstimatedMonthlyPayment(): boolean {
    return this._isShowEstimatedMonthlyPayment;
  }

  setVehiclePurchasePriceDraft(value: number | null): void {
    this.vehiclePurchasePriceDraftSubject.next(value);
  }
}
